import StationOnMapImage from 'images/station_on_map.png'
import { ILot } from 'utils/interfaces'
import { LOT_TYPE } from 'utils/lotInterfaces'

import type { ServiceCalibCatalogItem } from './serviceCalibTypes'

export const SERVICE_CALIB_ICON = {
  STATION: 'sc-station',
  LOT_NOT_SET: 'sc-lot-not-set',
  WASHING: 'sc-washing',
  CHARGING: 'sc-charging',
  PARKING: 'sc-parking',
  REFILLING: 'sc-refilling',
  DEBRIS_DUMP: 'sc-debris-dump',
  WASTE_WATER_DRAIN: 'sc-waste-water-drain',
  MAINTENANCE_UNIVERSAL: 'sc-maintenance-universal',
} as const

export type ServiceCalibIconId = typeof SERVICE_CALIB_ICON[keyof typeof SERVICE_CALIB_ICON]

/** Static assets in `public/service-calib-map/` (synced from `src/images/icons/*_map.svg` on build/start). */
const SERVICE_CALIB_MAP_ICON_BASE = `${process.env.PUBLIC_URL || ''}/service-calib-map`

export const SERVICE_CALIB_PIN_IMAGES: Array<{ name: ServiceCalibIconId; url: string }> = [
  { name: SERVICE_CALIB_ICON.LOT_NOT_SET, url: `${SERVICE_CALIB_MAP_ICON_BASE}/lot_not_set_map.svg` },
  { name: SERVICE_CALIB_ICON.WASHING, url: `${SERVICE_CALIB_MAP_ICON_BASE}/shower_map.svg` },
  { name: SERVICE_CALIB_ICON.CHARGING, url: `${SERVICE_CALIB_MAP_ICON_BASE}/plug_in_map.svg` },
  { name: SERVICE_CALIB_ICON.PARKING, url: `${SERVICE_CALIB_MAP_ICON_BASE}/parking_map.svg` },
  { name: SERVICE_CALIB_ICON.REFILLING, url: `${SERVICE_CALIB_MAP_ICON_BASE}/refilling_map.svg` },
  { name: SERVICE_CALIB_ICON.DEBRIS_DUMP, url: `${SERVICE_CALIB_MAP_ICON_BASE}/debris_dump_map.svg` },
  { name: SERVICE_CALIB_ICON.WASTE_WATER_DRAIN, url: `${SERVICE_CALIB_MAP_ICON_BASE}/waste_water_drain_map.svg` },
  {
    name: SERVICE_CALIB_ICON.MAINTENANCE_UNIVERSAL,
    url: `${SERVICE_CALIB_MAP_ICON_BASE}/maintenance_universal_on_map.svg`,
  },
]

/** Logical pin size at icon-size 1.0 — matches robot map PNG width (~42px), scaled up for visibility. */
export const SERVICE_CALIB_MAP_ICON_PX = 42
const MAP_ICON_RASTER_PX = SERVICE_CALIB_MAP_ICON_PX * 2
/** +30% vs robot marker at the same zoom. */
const SERVICE_CALIB_MAP_ICON_SCALE = 1.3

/** Highlighted pin is ~25–40% larger than default at the same zoom. */
export const SERVICE_CALIB_MAP_ICON_SIZE_LAYOUT: mapboxgl.Expression = [
  'interpolate',
  ['exponential', 2],
  ['zoom'],
  15,
  ['case', ['==', ['get', 'highlighted'], 1], 0.85 * SERVICE_CALIB_MAP_ICON_SCALE, 0.6 * SERVICE_CALIB_MAP_ICON_SCALE],
  22,
  ['case', ['==', ['get', 'highlighted'], 1], 4.0 * SERVICE_CALIB_MAP_ICON_SCALE, 3.2 * SERVICE_CALIB_MAP_ICON_SCALE],
]

/** Opacity for non-selected pins when a row is selected in the catalog. */
export const SERVICE_CALIB_MAP_UNSELECTED_OPACITY = 0.55

/** Full opacity when nothing selected; dim others only while a row is active. */
export const SERVICE_CALIB_MAP_PIN_OPACITY: mapboxgl.Expression = [
  'case',
  ['==', ['get', 'selectionActive'], 0],
  1,
  ['==', ['get', 'highlighted'], 1],
  1,
  SERVICE_CALIB_MAP_UNSELECTED_OPACITY,
]

export function getServiceCalibIconId(item: ServiceCalibCatalogItem): ServiceCalibIconId {
  if (item.target === 'maintenance_point') {
    if (item.maintenancePointType === 'debris_dump') {
      return SERVICE_CALIB_ICON.DEBRIS_DUMP
    }
    if (item.maintenancePointType === 'universal') {
      return SERVICE_CALIB_ICON.MAINTENANCE_UNIVERSAL
    }
    return SERVICE_CALIB_ICON.WASTE_WATER_DRAIN
  }

  switch (item.lotType) {
    case LOT_TYPE.CHARGING:
      return SERVICE_CALIB_ICON.CHARGING
    case LOT_TYPE.WASHING:
      return SERVICE_CALIB_ICON.WASHING
    case LOT_TYPE.PARKING:
      return SERVICE_CALIB_ICON.PARKING
    case LOT_TYPE.REFILLING:
      return SERVICE_CALIB_ICON.REFILLING
    case LOT_TYPE.DEBRIS_DUMP:
      return SERVICE_CALIB_ICON.DEBRIS_DUMP
    case LOT_TYPE.NOT_SET:
      return SERVICE_CALIB_ICON.LOT_NOT_SET
    default:
      return SERVICE_CALIB_ICON.LOT_NOT_SET
  }
}

export function resolveLotTypeToServiceCalibIconId(lotType: number | null | undefined): ServiceCalibIconId {
  switch (lotType) {
    case LOT_TYPE.CHARGING:
      return SERVICE_CALIB_ICON.CHARGING
    case LOT_TYPE.WASHING:
      return SERVICE_CALIB_ICON.WASHING
    case LOT_TYPE.PARKING:
      return SERVICE_CALIB_ICON.PARKING
    case LOT_TYPE.REFILLING:
      return SERVICE_CALIB_ICON.REFILLING
    case LOT_TYPE.DEBRIS_DUMP:
      return SERVICE_CALIB_ICON.DEBRIS_DUMP
    case LOT_TYPE.NOT_SET:
      return SERVICE_CALIB_ICON.LOT_NOT_SET
    default:
      return SERVICE_CALIB_ICON.LOT_NOT_SET
  }
}

/** Same pin logic as Robot → Service map: 1 lot → type icon, 2+ lots → aggregate station icon. */
export function resolveStationMapIconIdFromLots(lots: ILot[]): ServiceCalibIconId {
  if (lots.length > 1) {
    return SERVICE_CALIB_ICON.STATION
  }
  if (lots.length === 1) {
    return resolveLotTypeToServiceCalibIconId(lots[0].type)
  }
  return SERVICE_CALIB_ICON.STATION
}

function rasterizePin(source: CanvasImageSource): HTMLCanvasElement | null {
  const canvas = document.createElement('canvas')
  canvas.width = MAP_ICON_RASTER_PX
  canvas.height = MAP_ICON_RASTER_PX
  const ctx = canvas.getContext('2d')
  if (!ctx) {
    return null
  }
  ctx.clearRect(0, 0, MAP_ICON_RASTER_PX, MAP_ICON_RASTER_PX)
  ctx.drawImage(source, 0, 0, MAP_ICON_RASTER_PX, MAP_ICON_RASTER_PX)
  return canvas
}

function addPinImage(map: mapboxgl.Map, name: ServiceCalibIconId, source: CanvasImageSource): Promise<void> {
  if (map.hasImage(name)) {
    return Promise.resolve()
  }

  const add = (image: HTMLImageElement | ImageBitmap) => {
    if (map.hasImage(name)) {
      return
    }
    try {
      map.addImage(name, image, { pixelRatio: 2 })
    } catch (e) {
      console.warn('[ServiceCalibMap] map.addImage failed:', name, e)
    }
  }

  const raster = rasterizePin(source)
  if (raster) {
    return createImageBitmap(raster)
      .then(bitmap => add(bitmap))
      .catch(() => add(source as HTMLImageElement))
  }
  add(source as HTMLImageElement)
  return Promise.resolve()
}

export function loadServiceCalibPinImage(map: mapboxgl.Map, name: ServiceCalibIconId, url: string): Promise<void> {
  return new Promise(resolve => {
    if (map.hasImage(name)) {
      resolve()
      return
    }
    if (!url) {
      console.warn('[ServiceCalibMap] empty pin image url:', name)
      resolve()
      return
    }

    const img = new Image()
    img.onload = () => {
      void addPinImage(map, name, img).finally(() => resolve())
    }
    img.onerror = () => {
      console.warn('[ServiceCalibMap] failed to load pin image:', name, url)
      resolve()
    }
    img.src = url
  })
}

export function loadServiceCalibStationImage(map: mapboxgl.Map | undefined): Promise<void> {
  if (!map) {
    return Promise.resolve()
  }
  const name = SERVICE_CALIB_ICON.STATION
  return new Promise(resolve => {
    if (map.hasImage(name)) {
      resolve()
      return
    }
    map.loadImage(StationOnMapImage, (error, image) => {
      if (error || !image) {
        console.warn('[ServiceCalibMap] failed to load station map image', error)
        resolve()
        return
      }
      if (!map.hasImage(name)) {
        try {
          map.addImage(name, image)
        } catch (e) {
          console.warn('[ServiceCalibMap] map.addImage station failed:', e)
        }
      }
      resolve()
    })
  })
}

export function loadServiceCalibPinImages(map: mapboxgl.Map | undefined): Promise<void> {
  if (!map) {
    return Promise.resolve()
  }

  return Promise.all([
    loadServiceCalibStationImage(map),
    ...SERVICE_CALIB_PIN_IMAGES.map(({ name, url }) => loadServiceCalibPinImage(map, name, url)),
  ]).then(() => undefined)
}

export function registerServiceCalibStyleImageMissingHandler(map: mapboxgl.Map | undefined): () => void {
  if (!map) {
    return () => {}
  }

  const handler = (e: { id?: string }) => {
    const id = e?.id as ServiceCalibIconId | undefined
    if (!id) {
      return
    }
    if (id === SERVICE_CALIB_ICON.STATION) {
      if (!map.hasImage(id)) {
        void loadServiceCalibStationImage(map)
      }
      return
    }
    const entry = SERVICE_CALIB_PIN_IMAGES.find(x => x.name === id)
    if (!entry || map.hasImage(id)) {
      return
    }
    void loadServiceCalibPinImage(map, id, entry.url)
  }

  map.on('styleimagemissing', handler as never)
  return () => map.off('styleimagemissing', handler as never)
}
